import {
  SharesTransferExcelOperation,
  SharesTransferExcelRow,
  SharesTransferExcelValidationError,
} from '../types/SharesTransferExcel';

const rangesOverlap = (
  a: { shareFrom: number; shareTo: number },
  b: { shareFrom: number; shareTo: number }
): boolean =>
  a.shareFrom <= b.shareTo && b.shareFrom <= a.shareTo;

const validateNoOverlaps = (
  rows: SharesTransferExcelRow[],
  code: SharesTransferExcelValidationError['code'],
  operationId: string
): SharesTransferExcelValidationError[] => {
  const errors: SharesTransferExcelValidationError[] = [];
  const byCif = new Map<string, SharesTransferExcelRow[]>();

  rows.forEach((row) => {
    const group = byCif.get(row.cif) || [];
    group.push(row);
    byCif.set(row.cif, group);
  });

  byCif.forEach((group, cif) => {
    for (let i = 0; i < group.length; i += 1) {
      for (let j = i + 1; j < group.length; j += 1) {
        if (rangesOverlap(group[i], group[j])) {
          errors.push({
            code,
            operationId,
            rowIndex: group[j].rowIndex,
            cif,
          });
        }
      }
    }
  });

  return errors;
};

const mergeAdjacentShareRanges = (
  ranges: Array<{ shareFrom: number; shareTo: number }>
): Array<{ shareFrom: number; shareTo: number }> => {
  if (!ranges.length) {
    return [];
  }

  const sorted = [...ranges].sort(
    (firstRange, secondRange) =>
      firstRange.shareFrom - secondRange.shareFrom ||
      firstRange.shareTo - secondRange.shareTo
  );

  const merged = [
    { shareFrom: sorted[0].shareFrom, shareTo: sorted[0].shareTo },
  ];

  for (let index = 1; index < sorted.length; index += 1) {
    const currentRange = sorted[index];
    const lastMergedRange = merged[merged.length - 1];

    if (currentRange.shareFrom <= lastMergedRange.shareTo + 1) {
      lastMergedRange.shareTo = Math.max(
        lastMergedRange.shareTo,
        currentRange.shareTo
      );
    } else {
      merged.push({
        shareFrom: currentRange.shareFrom,
        shareTo: currentRange.shareTo,
      });
    }
  }

  return merged;
};

const isBuyerCoveredBySellers = (
  sellers: SharesTransferExcelRow[],
  buyer: SharesTransferExcelRow
): boolean => {
  const overlappingSellers = sellers.filter((seller) =>
    rangesOverlap(seller, buyer)
  );
  const merged = mergeAdjacentShareRanges(overlappingSellers);

  let cursor = buyer.shareFrom;

  for (let index = 0; index < merged.length; index += 1) {
    const range = merged[index];

    if (range.shareFrom > cursor) {
      return false;
    }

    cursor = Math.max(cursor, range.shareTo + 1);

    if (cursor > buyer.shareTo) {
      return true;
    }
  }

  return cursor > buyer.shareTo;
};

const findContainingSeller = (
  sellers: SharesTransferExcelRow[],
  buyer: SharesTransferExcelRow
): SharesTransferExcelRow | null => {
  const singleContainer =
    sellers.find(
      (seller) =>
        seller.shareFrom <= buyer.shareFrom && seller.shareTo >= buyer.shareTo
    ) || null;

  if (singleContainer) {
    return singleContainer;
  }

  if (!isBuyerCoveredBySellers(sellers, buyer)) {
    return null;
  }

  const overlappingSellers = sellers
    .filter((seller) => rangesOverlap(seller, buyer))
    .sort(
      (firstSeller, secondSeller) =>
        firstSeller.shareFrom - secondSeller.shareFrom
    );

  const coveringCifs = [
    ...new Set(overlappingSellers.map((seller) => seller.cif)),
  ];

  if (coveringCifs.length === 1) {
    return overlappingSellers[0] || null;
  }

  return overlappingSellers[0] || null;
};

export const groupSharesTransferExcelByOperation = (
  rows: SharesTransferExcelRow[]
): SharesTransferExcelOperation[] => {
  const grouped = new Map<string, SharesTransferExcelRow[]>();

  rows.forEach((row) => {
    const group = grouped.get(row.operationId) || [];
    group.push(row);
    grouped.set(row.operationId, group);
  });

  const operations: SharesTransferExcelOperation[] = [];

  grouped.forEach((operationRows, operationId) => {
    const errors: SharesTransferExcelValidationError[] = [];
    const sellers = operationRows.filter((row) => row.isSeller);
    const buyers = operationRows.filter((row) => !row.isSeller);
    const operationTypes = [
      ...new Set(operationRows.map((row) => row.operationType)),
    ];
    const dates = [...new Set(operationRows.map((row) => row.date.getTime()))];

    if (dates.length > 1) {
      errors.push({
        code: 'INVALID_DATE',
        operationId,
        detail: 'MULTIPLE_DATES',
      });
    }

    if (!sellers.length) {
      errors.push({ code: 'NO_SELLERS', operationId });
    }

    if (!buyers.length) {
      errors.push({ code: 'NO_BUYERS', operationId });
    }

    const negativeSum = sellers.reduce(
      (sum, row) => sum + Math.abs(row.totalShares),
      0
    );
    const positiveSum = buyers.reduce((sum, row) => sum + row.totalShares, 0);

    if (negativeSum !== positiveSum) {
      errors.push({ code: 'UNBALANCED_SHARES', operationId });
    }

    errors.push(
      ...validateNoOverlaps(
        sellers,
        'OVERLAPPING_SELLER_RANGES',
        operationId
      ),
      ...validateNoOverlaps(buyers, 'OVERLAPPING_BUYER_RANGES', operationId)
    );

    buyers.forEach((buyer) => {
      if (!isBuyerCoveredBySellers(sellers, buyer)) {
        errors.push({
          code: 'BUYER_WITHOUT_SELLER',
          operationId,
          rowIndex: buyer.rowIndex,
          cif: buyer.cif,
        });
      }
    });

    const needsTypeSelection = operationTypes.length > 1;
    const selectedOperationType = needsTypeSelection ? null : operationTypes[0];

    if (needsTypeSelection) {
      errors.push({ code: 'MIXED_OPERATION_TYPES', operationId });
    }

    operations.push({
      operationId,
      date: operationRows[0]?.date ?? new Date(0),
      operationTypes,
      selectedOperationType,
      needsTypeSelection,
      rows: operationRows,
      sellers,
      buyers,
      errors,
      isValid: errors.length === 0,
    });
  });

  return operations.sort((a, b) => {
    const dateDiff = a.date.getTime() - b.date.getTime();
    if (dateDiff !== 0) return dateDiff;
    return a.operationId.localeCompare(b.operationId);
  });
};

export { findContainingSeller, isBuyerCoveredBySellers };
